<template>
  <div class="login">
    <div class="container">
      <div class="row row-lg-right row-xl-right">
        <div class="col col-xs-12 col-md-6 col-lg-4 col-lg-6">
          <div class="login__card">
            <el-card class="box-card" :body-style="{ padding: '40px 36px' }">
              <h1 class="login__title">
                Reset password
              </h1>

              <div class="login__no-account login__no-account--padding-bottom">
                <router-link :to="{ name: 'Login' }">
                  <el-link>Back to login</el-link>
                </router-link>
              </div>

              <div class="login__form">
                <el-form
                  @submit.native.prevent="validateForm"
                  label-width="0"
                  :rules="resetPasswordRules"
                  :model="resetPasswordForm"
                  ref="resetPasswordForm"
                >
                  <el-form-item :error="errors.email" prop="email">
                    <el-input
                      v-model="resetPasswordForm.email"
                      placeholder="Email"
                      :disabled="Boolean(confirmationEmail)"
                    />
                  </el-form-item>

                  <el-form-item
                    v-if="Boolean(confirmationCode)"
                    :error="errors.password"
                    prop="password"
                  >
                    <el-input
                      v-model="resetPasswordForm.password"
                      placeholder="Password"
                      show-password
                    />
                  </el-form-item>

                  <el-form-item
                    v-if="Boolean(confirmationCode)"
                    :error="errors.confirmPassword"
                    prop="confirmPassword"
                  >
                    <el-input
                      v-model="resetPasswordForm.confirmPassword"
                      placeholder="Re-enter password"
                      show-password
                    />
                  </el-form-item>

                  <el-button
                    type="primary"
                    native-type="submit"
                    class="login__submit-button"
                    :disabled="
                      isButtonDisabled('resetPassword') ||
                        (!Boolean(confirmationCode) && isCodeSent)
                    "
                    @click="validateForm"
                  >
                    {{ buttonLabel }}
                  </el-button>
                </el-form>

                <p v-if="!Boolean(confirmationCode) && isCodeSent">
                  Please check your email. Follow the link we sent to reset your
                  password
                </p>
              </div>
            </el-card>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Icon from '@/components/common/Icon.vue';

import { resetPassword, sendResetPasswordCode } from '@/api/user.js';
import { disableButton } from '@/mixins/disableButton.js';
import { showMessage } from '@/utils/messages.js';
import { parseErrorsByFields } from '@/utils/index.js';

export default {
  name: 'PasswordReset',
  components: {
    Icon,
  },
  mixins: [disableButton],
  data() {
    return {
      resetPasswordForm: {
        email: '',
        password: '',
        confirmPassword: '',
      },
      isCodeSent: false,
      errors: {
        email: '',
        password: '',
        confirmPassword: '',
      },
      resetPasswordRules: {
        email: [
          {
            required: true,
            trigger: 'input',
            message: 'Please enter email',
          },
        ],
        password: [
          {
            required: true,
            trigger: 'input',
            message: 'Please enter password',
          },
        ],
        confirmPassword: [
          {
            required: true,
            trigger: 'input',
            message: 'Please confirm password',
          },
        ],
      },
    };
  },
  computed: {
    buttonLabel() {
      const { isCodeSent, confirmationCode } = this;
      return isCodeSent && confirmationCode ? 'Reset password' : 'Send code';
    },
    isFormFilled() {
      const { resetPasswordForm } = this;
      return Object.values(resetPasswordForm).every((value) => !!value);
    },
    confirmationCode() {
      return this.$route.query.code || null;
    },
    confirmationEmail() {
      return this.$route.query.email || null;
    },
  },
  methods: {
    showMessage,
    parseErrorsByFields,
    async requestResetPassword() {
      this.disableButton('resetPassword');
      const { confirmationCode } = this;
      const { email, password, confirmPassword } = this.resetPasswordForm;
      const { success, errors } = await resetPassword({
        email,
        password,
        confirmPassword,
        code: confirmationCode,
      });
      this.enableButton('resetPassword');

      if (errors) {
        this.errors = { ...this.errors, ...this.parseErrorsByFields(errors) };
      } else if (success) {
        this.showMessage({
          type: 'success',
          message: 'Your password has been changed successfully.',
        });
        this.clearForm();
        this.clearErrors();
      }
    },
    async requestSendCode() {
      this.disableButton('resetPassword');
      const { email } = this.resetPasswordForm;
      const { success, errors } = await sendResetPasswordCode({ email });
      this.enableButton('resetPassword');

      if (errors) {
        this.errors = { ...this.errors, ...this.parseErrorsByFields(errors) };
      } else if (success) {
        this.showMessage({
          type: 'success',
          message: 'Confirmation code has been sent to your email.',
        });
        this.clearErrors();
        this.isCodeSent = true;
      }
    },
    validateForm() {
      const {
        isCodeSent,
        resetPasswordForm,
        isFormFilled,
        confirmationCode,
      } = this;

      if (!isCodeSent) {
        if (resetPasswordForm.email && resetPasswordForm.email.trim()) {
          this.requestSendCode();
        } else {
          this.showMessage({
            type: 'error',
            message: 'Please fill in all fields',
          });
        }
      } else {
        if (Boolean(confirmationCode)) {
          if (this.$refs.resetPasswordForm) {
            this.$refs.resetPasswordForm.validate();
          }
          if (isFormFilled) {
            this.requestResetPassword();
          }
        } else {
          this.errors = {
            ...this.errors,
            code:
              'Please check your email. Follow the link we sent to reset your password',
          };
        }
      }
    },
    clearForm() {
      const { resetPasswordForm } = this;
      for (const fieldName in resetPasswordForm) {
        resetPasswordForm[fieldName] = '';
      }
    },
    clearErrors() {
      const { errors } = this;
      for (const fieldName in errors) {
        errors[fieldName] = '';
      }
    },
  },
  created() {
    const { confirmationEmail, confirmationCode } = this;
    if (confirmationEmail) {
      this.resetPasswordForm.email = confirmationEmail;
    }

    if (confirmationCode) {
      this.isCodeSent = true;
    }
  },
};
</script>
